import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SessionChangedError } from 'config/axiosInstance';
import { clearSession, getSessionIdentity } from 'config/session';
import { formatBusiness } from 'config/time';
import { LOGIN_PATH, OPERATOR_PATH, operatorCompanyPath } from 'router/pathName';
import { useOperatorErrors } from 'components/Operator/OperatorUi';
import {
  forgetSupportAccess,
  returnFromSupport,
  SupportAccess,
  SupportSessionLostError,
  supportNavigation,
  SupportStatus,
} from './supportApi';

/**
 * YAC-2026-009 PR7b。運営のサポートの画面の共通の部品（帯・プロフィール・終わりの画面・運営の画面で使う）。
 * 配色は既存の知らせ（OperatorUi の ErrorNotice・StatusBadge）と同じ、4.5 以上の組だけを使う。
 */

type Failure = { response?: { status?: number; data?: { code?: unknown } } };
const statusOf = (error: unknown) => (error as Failure)?.response?.status;
const codeOf = (error: unknown) => {
  const code = (error as Failure)?.response?.data?.code;
  return typeof code === 'string' ? code : null;
};

/** 期限の表示（「10月26日 14:30」）。業務の時間帯（管理者の画面は会社の時間帯）で出す。 */
export function useSupportDate() {
  const { t } = useTranslation('layout');
  return useCallback(
    (value: number | string | null | undefined) =>
      value === null || value === undefined ? '' : formatBusiness(new Date(value), t('support.date-format')),
    [t]
  );
}

/**
 * 運営の画面に戻る（帯・プロフィール・終わりの画面）。
 * - 成功: 戻った会社の詳細（運営の画面）を開き直す。
 * - 401 operator_sign_in_required（運営のログインの締め切りを過ぎた・運営でなくなった・認証アプリが変わった・
 *   サポート中にパスワードが再設定された。Cookie はサーバーが消した）: 印を消し、運営のログインの画面へ送る。
 * - 戻った後のセッションを使えなかった: 印を消し、もう一度ログインしてもらう（案内を出す）。
 * - 401 のほか（Cookie が無い・使い終わり）: 別のタブで先に戻っていれば（印が替わった）運営の画面を開く。
 *   そうでなければ、もう一度ログインしてもらう。
 * - 通信できない・Web Locks が無いなど: 文を出して、押し直せるようにする（セッションはそのまま）。
 */
export function useSupportReturn() {
  const { t } = useTranslation('layout');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signIn, setSignIn] = useState(false);

  const run = useCallback(
    async (end: boolean) => {
      if (busy) return;
      setBusy(true);
      setError(null);
      const before = getSessionIdentity();
      try {
        const record = await returnFromSupport(end);
        supportNavigation.assign(operatorCompanyPath(record.company.id));
        return;
      } catch (failure) {
        console.error('Failed to return from support', failure);
        const lost = failure instanceof SupportSessionLostError;
        if (lost || statusOf(failure) === 401) {
          const code = codeOf(failure);
          if (!lost && code === 'operator_sign_in_required') {
            if (getSessionIdentity() === before) clearSession();
            forgetSupportAccess();
            supportNavigation.assign(LOGIN_PATH);
            return;
          }
          if (!lost && getSessionIdentity() !== before) {
            // 別のタブで先に戻った（Cookie は運営のセッションのもの）。
            supportNavigation.assign(OPERATOR_PATH);
            return;
          }
          if (getSessionIdentity() === before) clearSession();
          forgetSupportAccess();
          setSignIn(true);
        } else {
          setError(t('support.return-failed'));
        }
      }
      setBusy(false);
    },
    [busy, t]
  );

  return { busy, error, signIn, run };
}

/** もう一度ログインしてもらう（運営の画面に戻れない）。 */
export const SignInAgain: React.FC = () => {
  const { t } = useTranslation('layout');
  return (
    <div role="alert" className="flex flex-col gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-body-2 text-red-700">
      <p>{t('support.sign-in-again')}</p>
      <a href={LOGIN_PATH} className="font-medium underline">
        {t('support.sign-in')}
      </a>
    </div>
  );
};

/** 入るときの失敗を、画面の言語の文にする。 */
export function useEnterErrors() {
  const { t } = useTranslation('layout');
  const operatorErrors = useOperatorErrors();
  return useCallback(
    (failure: unknown): { messages: string[]; signIn: boolean } => {
      if (failure instanceof SupportSessionLostError) {
        return { messages: [t('support.error-operator-signed-out')], signIn: true };
      }
      if (failure instanceof SessionChangedError || codeOf(failure) === 'session_mismatch') {
        return { messages: [t('support.error-session-mismatch')], signIn: false };
      }
      if (statusOf(failure) === 401) {
        return { messages: [t('support.error-operator-signed-out')], signIn: true };
      }
      // #393。削除済みの会社には入れない（404）。
      if (statusOf(failure) === 404) {
        return { messages: [t('support.error-company-not-found')], signIn: false };
      }
      return { messages: operatorErrors(failure), signIn: false };
    },
    [t, operatorErrors]
  );
}

const STATUS_CLASS: Record<SupportStatus, string> = {
  active: 'bg-green-50 text-green-800 border-green-200',
  ended: 'bg-gray-100 text-gray-700 border-gray-300',
  expired: 'bg-red-50 text-red-700 border-red-200',
};

/** サポートの状態（有効・終了・期限切れ）。 */
export const SupportStatusBadge: React.FC<{ status: SupportStatus }> = ({ status }) => {
  const { t } = useTranslation('layout');
  return (
    <span className={`inline-block whitespace-nowrap rounded border px-2 py-0.5 text-caption ${STATUS_CLASS[status] ?? STATUS_CLASS.ended}`}>
      {t(`support.status-${status}`)}
    </span>
  );
};

/** 終わった理由（だれが終わらせたか・期限・運営から外れた）。開いている記録は空。 */
export function useEndReason() {
  const { t } = useTranslation('layout');
  return useCallback(
    (access: SupportAccess): string => {
      if (access.end_reason === 'ended') {
        return access.ended_by?.name
          ? t('support.reason-ended', { name: access.ended_by.name })
          : t('support.reason-ended-unknown');
      }
      if (access.end_reason === 'expired') return t('support.reason-expired');
      if (access.end_reason === 'operator_revoked') return t('support.reason-operator_revoked');
      // #393。サポート中・サポートの記録が開いている間に、会社が削除された。
      if (access.end_reason === 'company_deleted') return t('support.reason-company_deleted');
      return '';
    },
    [t]
  );
}
